import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    Center,
    Flex,
    Box,
    Grid,
    GridItem,
    Tag,
    Text,
    Stack,
    Skeleton,
    Separator,
    Dialog,
    Portal,
    CloseButton
} from "@chakra-ui/react";
import { ArrowRightIconSM } from "@digital-services-gard-as/anchor-icons";
import { Button } from "@digital-services-gard-as/anchor-react";
import { RelatedCompanyDto } from "@/api/usersApi";
import { TruncatedText } from "@/components/TruncatedText";

type ActOnBehalfOfModalProps = {
    isOpen: boolean;
    onClose: () => void;
    isLoading?: boolean;
    isError?: boolean;
    companies: RelatedCompanyDto[];
    setActingOnBehalfOf: (companyName: RelatedCompanyDto) => void;
    selectedActingOnBehalfOf?: RelatedCompanyDto;
};

export const ActOnBehalfOfModal = ({
    isOpen,
    onClose,
    isError,
    isLoading,
    selectedActingOnBehalfOf,
    companies,
    setActingOnBehalfOf
}: ActOnBehalfOfModalProps) => {
    const { t } = useTranslation(["claimsInvoicesOverview", "main"]);

    const handleSelectActOnBehalfOf = useCallback(
        (actingBehalfOf: RelatedCompanyDto) => {
            setActingOnBehalfOf(actingBehalfOf);
            onClose();
        },
        [onClose, setActingOnBehalfOf]
    );

    // Handler for select button, avoids inline arrow in JSX
    const handleSelectBtnClick = useCallback(
        (company: RelatedCompanyDto) => () => handleSelectActOnBehalfOf(company),
        [handleSelectActOnBehalfOf]
    );

    const handleOpenChange = useCallback(
        (e: { open: boolean }) => {
            if (!e.open) {
                onClose();
            }
        },
        [onClose]
    );

    return (
        <Dialog.Root open={isOpen} size="lg" onOpenChange={handleOpenChange}>
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content p="1rem" data-testid="actOnBehalfOfModal">
                        <Dialog.Header textAlign="left">
                            <Dialog.Title>{t("claimsInvoicesOverview:actOnBehalfOfModal.title")}</Dialog.Title>
                        </Dialog.Header>
                        <Dialog.CloseTrigger asChild id="actOnBehalfOfModalClose">
                            <CloseButton size="sm" />
                        </Dialog.CloseTrigger>
                        <Dialog.Body gap="2rem" display="flex" flexDirection="column">
                            {isLoading && (
                                <Stack>
                                    <Skeleton height="20px" />
                                    <Skeleton height="20px" />
                                    <Skeleton height="20px" />
                                </Stack>
                            )}
                            {isError && (
                                <Center>
                                    <Text>{t("claimsInvoicesOverview:branchCompanyModal.noData")}</Text>
                                </Center>
                            )}
                            {!companies && (
                                <Center>
                                    <Text>{t("claimsInvoicesOverview:actOnBehalfOfModal.noData")}</Text>
                                </Center>
                            )}
                            <Flex flexDir="column">
                                {companies?.map((company, index) => (
                                    <Box key={company.crmId}>
                                        <Flex alignItems="center" justify="space-between">
                                            <Grid
                                                templateRows="repeat(1, 1fr)"
                                                templateColumns="repeat(2, 1fr)"
                                                gap={2}
                                            >
                                                <GridItem w="12rem">
                                                    <Text asChild>
                                                        <b>{t("claimsInvoicesOverview:actOnBehalfOfModal.company")}</b>
                                                    </Text>
                                                    <TruncatedText text={company.companyName} />
                                                </GridItem>
                                                <GridItem ml="2rem">
                                                    <Text asChild>
                                                        <b>{t("claimsInvoicesOverview:actOnBehalfOfModal.city")}</b>
                                                    </Text>
                                                    <Box maxW="12rem">
                                                        <TruncatedText text={company.billingCity} />
                                                    </Box>
                                                </GridItem>
                                            </Grid>
                                            <Flex>
                                                {selectedActingOnBehalfOf?.crmId === company.crmId && (
                                                    <Tag.Root size="sm" variant="solid" colorPalette="blue">
                                                        {t("claimsInvoicesOverview:actOnBehalfOfModal.selected")}
                                                    </Tag.Root>
                                                )}
                                            </Flex>
                                            <Flex>
                                                <Button
                                                    data-testid="selectBtn"
                                                    size="sm"
                                                    icon={<ArrowRightIconSM />}
                                                    variant="icon-only"
                                                    aria-label="Select company"
                                                    onClick={handleSelectBtnClick(company)}
                                                />
                                            </Flex>
                                        </Flex>
                                        {companies.length - 1 > index && <Separator />}
                                    </Box>
                                ))}
                            </Flex>
                            <Flex flexDir="column" gap="1rem" mt="1rem">
                                <Button data-testid="cancelBtn" variant="tertiary" onClick={onClose}>
                                    {t("main:cancel")}
                                </Button>
                            </Flex>
                        </Dialog.Body>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};
